import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useBoundStore } from '@Store/index';
import { MAIN_INCOME_SOURCE } from '@Types/consts';
import { BUSINESS_FILE_TYPES } from '@Store/slices/kyb/kyb.types';
import { useGetBusinessOwners } from '@Pages/happyFlowPages/ownersCenter/hooks/useGetBusinessOwnersData';
import type { MissingFileForNonUsUbo } from '@Store/slices/kyb/kyb.types';

export interface IcontainContent {
  type: keyof typeof BUSINESS_FILE_TYPES;
  title: string;
  texts: { positive: string[]; negative: string[]; description?: string };
}

export interface DocumentRequest {
  type: 'LLC' | 'NON_US_UBO';
  fileType: keyof typeof BUSINESS_FILE_TYPES;
  uboName?: string;
  externalId?: string;
}

export const useVerificationCenterHook = () => {
  const { t } = useTranslation();
  const { kybData } = useBoundStore.getState();
  const { mainOwner } = useGetBusinessOwners();
  const [activeStep, setActiveStep] = useState(0);
  const mainIncomeSource = useBoundStore(
    (state) => state.customer.customerData.customerBusinessData.mainIncomeSource
  );

  const allMissingDocuments = useMemo(() => {
    const llcDocs: DocumentRequest[] = kybData.business.missingLlcDocuments
      .filter((fileType) => fileType !== 'PASSPORT')
      .map((fileType) => ({
        type: 'LLC',
        fileType,
      }));
    const uboDocs: DocumentRequest[] =
      kybData.business.missingFileForNonUsUbos?.map((uboFile: MissingFileForNonUsUbo) => ({
        type: 'NON_US_UBO',
        fileType: uboFile.fileType,
        uboName: uboFile.name,
        externalId: uboFile.externalId,
      })) || [];
    return [...llcDocs, ...uboDocs];
  }, [kybData]);

  const activeDocumentRequest = allMissingDocuments[activeStep];

  const [steps, setSteps] = useState({
    numberOfSteps: allMissingDocuments.length,
    activeStepName: activeDocumentRequest?.fileType as keyof typeof BUSINESS_FILE_TYPES,
  });

  useEffect(() => {
    setSteps({
      numberOfSteps: allMissingDocuments.length,
      activeStepName: allMissingDocuments[activeStep]?.fileType,
    });
  }, [activeStep, allMissingDocuments]);

  const getTitle = () => {
    if (!activeDocumentRequest) return '';
    if (activeDocumentRequest.fileType === BUSINESS_FILE_TYPES.REQUEST_FOR_INFORMATION) {
      return 'Request for Information';
    }
    const titleKey =
      mainIncomeSource === MAIN_INCOME_SOURCE.NON_PROFIT &&
      activeDocumentRequest.fileType === BUSINESS_FILE_TYPES.ARTICLES_OF_INCORPORATION
        ? (`pages.verificationCenter.documents.${activeDocumentRequest.fileType}_NON_PROFIT.title` as const)
        : (`pages.verificationCenter.documents.${activeDocumentRequest.fileType}.title` as const);
    const fileTypeTitle = t(titleKey);
    if (activeDocumentRequest.type === 'NON_US_UBO') {
      return `Upload ${fileTypeTitle} of: ${activeDocumentRequest.uboName}`;
    }
    return fileTypeTitle;
  };

  const getTexts = () => {
    if (
      mainIncomeSource === MAIN_INCOME_SOURCE.NON_PROFIT &&
      activeDocumentRequest.fileType === BUSINESS_FILE_TYPES.ARTICLES_OF_INCORPORATION
    ) {
      return `pages.verificationCenter.documents.${steps.activeStepName}_NON_PROFIT.texts` as const;
    }
    const isSecondaryNonUsUboPassport =
      activeDocumentRequest.fileType === BUSINESS_FILE_TYPES.PASSPORT &&
      activeDocumentRequest.type === 'NON_US_UBO' &&
      activeDocumentRequest.externalId !== mainOwner?.externalId;
    if (isSecondaryNonUsUboPassport) {
      return `pages.verificationCenter.documents.${steps.activeStepName}_SECONDARY_UBO.texts` as const;
    }
    return `pages.verificationCenter.documents.${steps.activeStepName}.texts` as const;
  };

  const title = getTitle();
  const textKey = getTexts();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = t(textKey as any, {
    returnObjects: true,
  }) as { positive: string[]; negative: string[]; description?: string };
  const texts = {
    positive: result.positive ?? [],
    negative: result.negative ?? [],
    description: result.description,
  };
  const documents = { title, texts };

  const additionalContent = useMemo(() => {
    const data = kybData.business.requestedDocsData;
    if (!data?.length) return null;

    const matchingRequest = data.find(
      (docRequest) => docRequest.llcFileType === activeDocumentRequest?.fileType
    );

    if (!matchingRequest) return null;

    return {
      header: matchingRequest.header,
      message: matchingRequest.message,
      instructions: matchingRequest.instructions,
      docRequestReasonEnum: matchingRequest.docRequestReasonEnum,
    };
  }, [activeDocumentRequest, kybData]);

  return {
    steps,
    setSteps,
    setActiveStep,
    activeStep,
    documents,
    activeDocumentRequest,
    additionalContent,
    allMissingDocuments,
  };
};
